// THE USER'S SAVED LANGUAGE, as the web reads and writes it (spec 16 §3.10,
// D-430). lib/locale.ts decides which language wins; this file holds the one
// profile read every mounted switcher shares, and the save that follows a
// switcher choice.
//
// ONE READ PER SIGNED-IN USER, NOT PER PAGE. Sign-in and sign-out are in-app
// navigations, so a module cache outlives the person it was read for. On a
// shared floor tablet that handed worker A's profile, and A's language, to
// worker B until a hard reload. `resetProfileRead` is called on sign-in and on
// sign-out, and a read that failed (a 401 before sign-in, no signal) is not
// remembered at all, so the next mount asks again.
//
// A SAVE THAT DID NOT LAND IS KEPT, NOT DROPPED. The device already holds the
// choice; the profile is what carries it to another device and to email. If
// the PATCH fails (offline, a password that must change first) the choice is
// recorded as unsaved (LOCALE_UNSAVED_KEY), which stops the old saved value
// from taking the choice back, and it is sent again on the switcher's next
// mount and when the device comes back online.
//
// AN IMPERSONATION SESSION SAVES NOTHING. A staff member who changes the UI
// language is choosing their own reading language, and saving it would change
// the language of the impersonated user's mail and Telegram replies.

import type { Locale } from "@assessly/contracts/locales";
import { client } from "./api.js";
import {
  clearUnsavedChoice,
  readUnsavedChoice,
  unsavedChoiceFor,
  writeUnsavedChoice,
  type ProfileLocaleFacts,
  type UnsavedLocaleChoice,
} from "./locale.js";

let profileRead: Promise<ProfileLocaleFacts | undefined> | undefined;

/** Counts sign-ins and sign-outs on this page. A query cache that keys a
 * profile-derived read on it (the home's "mine" filters) asks again for the
 * next person, because sign-in is a client-side navigation and the cache
 * outlives the person it was read for. */
let profileEpoch = 0;

/** Which signed-in person this page is on, as a number that changes at every
 * sign-in and sign-out. */
export function profileReadEpoch(): number {
  return profileEpoch;
}

/** The session user's profile, read once and shared until sign-in or sign-out. */
export function readProfileOnce(): Promise<ProfileLocaleFacts | undefined> {
  if (profileRead !== undefined) return profileRead;
  // Started inside a promise, so a client that throws synchronously (a test
  // double without GET) also ends as "no profile": a failed read never breaks
  // the shell it sits in.
  const read: Promise<ProfileLocaleFacts | undefined> = Promise.resolve()
    .then(() => client.GET("/v1/auth/profile"))
    .then(
      (result) => (result.response.ok ? result.data : undefined),
      () => undefined,
    );
  profileRead = read;
  void read.then((profile) => {
    if (profile === undefined && profileRead === read) profileRead = undefined;
  });
  return read;
}

/**
 * Forget the profile read. Called on sign-in and on sign-out, so the next
 * person on the device reads their own. A choice that could not be tied to a
 * user goes with it; a choice tied to a user stays for that user's next
 * sign-in.
 */
export function resetProfileRead(): void {
  profileRead = undefined;
  profileEpoch += 1;
  if (readUnsavedChoice()?.user_id === null) clearUnsavedChoice();
}

/** Send one choice to the profile. True when the profile accepted it. */
async function sendChoice(choice: UnsavedLocaleChoice): Promise<boolean> {
  try {
    const result = await client.PATCH("/v1/auth/profile", { body: { locale: choice.locale } });
    if (!result.response.ok || result.data === undefined) return false;
    const saved = result.data;
    profileRead = Promise.resolve(saved);
    const stored = readUnsavedChoice();
    if (
      stored?.locale === choice.locale &&
      (stored.user_id === null || stored.user_id === saved.user_id)
    ) {
      clearUnsavedChoice();
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Save a switcher choice to the user's profile. The unsaved marker is written
 * before anything is awaited, so a profile read that lands in between cannot
 * put the old saved language back.
 */
export async function saveLocaleChoice(locale: Locale): Promise<boolean> {
  writeUnsavedChoice({ user_id: null, locale });
  const profile = await readProfileOnce();
  if (profile?.impersonated === true) {
    clearUnsavedChoice();
    return false;
  }
  const choice: UnsavedLocaleChoice = { user_id: profile?.user_id ?? null, locale };
  // Another choice may have been made while the profile was being read.
  if (readUnsavedChoice()?.locale !== locale) return false;
  writeUnsavedChoice(choice);
  return sendChoice(choice);
}

/** Send this user's unsaved choice again, if there is one. */
export function retryUnsavedChoice(profile: ProfileLocaleFacts): Promise<boolean> {
  const choice = unsavedChoiceFor(profile.user_id);
  if (choice === undefined || profile.impersonated) return Promise.resolve(false);
  return sendChoice({ user_id: profile.user_id, locale: choice.locale });
}

/** Test seam: forget the cached read without touching storage. */
export function __resetProfileReadForTests(): void {
  profileRead = undefined;
}
